import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router';
import { useAuth } from './auth/AuthContext';
import { AccountPage, ActivityPage, PasswordPage, TokensPage } from './pages/Account';
import { DocumentsPage } from './pages/DocumentsPage';
import { ContentProvider } from './content/ContentContext';
import { GameHome } from './pages/GameHome';
import { Games } from './pages/Games';
import { Organizations } from './pages/Organizations';
import { SignIn } from './pages/SignIn';
import { SignUp } from './pages/SignUp';
import { AcceptInvitePage } from './pages/AcceptInvite';
import { ForgotPassword, ResetPassword } from './pages/PasswordReset';
import { TemplatesPage } from './pages/TemplatesPage';
import { EnumEditorPage } from './pages/schema/EnumEditorPage';
import { EnumsPage } from './pages/schema/EnumsPage';
import { ProfilesPage } from './pages/schema/ProfilesPage';
import { TagsPage } from './pages/schema/TagsPage';
import { TemplateEditorPage } from './pages/schema/TemplateEditorPage';
import { SearchPage } from './pages/SearchPage';
import { ExtensionsProvider } from './extensions/ExtensionsContext';
import { AssetsPage } from './pages/assets/AssetsPage';
import { ExtensionsPage } from './pages/extensions/ExtensionsPage';
import { EnvironmentSettingsPage } from './pages/env/EnvironmentSettingsPage';
import { PlatformsPage } from './pages/env/PlatformsPage';
import { OrdersPage } from './pages/orders/OrdersPage';
import { HistoryPage } from './pages/history/HistoryPage';
import { MergeRequestPage, MergeRequestsPage } from './pages/merge/MergeRequestsPage';
import { OrgPage } from './pages/org/OrgPage';
import { ReleasesPage } from './pages/releases/ReleasesPage';
import { SettingsPage } from './pages/settings/SettingsPage';
import { PlayerPage } from './pages/players/PlayerPage';
import { PlayersPage } from './pages/players/PlayersPage';
import { ReceiptPage } from './pages/players/ReceiptPage';
import { LocalizationPage } from './pages/localization/LocalizationPage';
import { ProductsPage } from './pages/products/ProductsPage';
import { AbTestsPage } from './pages/liveops/AbTestsPage';
import { CalendarPage } from './pages/liveops/CalendarPage';
import { DailyBonusPage } from './pages/liveops/DailyBonusPage';
import { EventsPage } from './pages/liveops/EventsPage';
import { LiveOpsSettingsHost } from './pages/liveops/LiveOpsSettingsDialog';
import { OffersPage } from './pages/liveops/OffersPage';
import { OverridesPage } from './pages/liveops/OverridesPage';
import { SalesPage } from './pages/liveops/SalesPage';
import { SegmentsPage } from './pages/liveops/SegmentsPage';
import { StorePage } from './pages/liveops/StorePage';
import { NotFoundPage } from './shell/ErrorPage';
import { GameLayout } from './shell/GameLayout';
import { PlainLayout } from './shell/PlainLayout';
import { LeaveGuard } from './ui/LeaveGuard';

function RequireSession() {
  const { session } = useAuth();
  const location = useLocation();
  // only a missing token sends the user to sign-in; a failed request never does (spec section 3.2)
  if (!session) return <Navigate to="/sign-in" replace state={{ from: location.pathname + location.search }} />;
  return <Outlet />;
}

function BranchIndex() {
  return <Navigate to="templates" replace />;
}

export function App() {
  return (
    <>
    <LeaveGuard />
    <Routes>
      <Route path="/sign-in" element={<SignIn />} />
      <Route path="/sign-up" element={<SignUp />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route element={<RequireSession />}>
        <Route element={<PlainLayout />}>
          <Route index element={<Navigate to="/orgs" replace />} />
          <Route path="/orgs" element={<Organizations />} />
          <Route path="/orgs/:org/games" element={<Games />} />
          <Route path="/orgs/:org/members" element={<OrgPage />} />
          <Route path="/orgs/:org/keys" element={<OrgPage />} />
          <Route path="/orgs/:org/integrations" element={<OrgPage />} />
          <Route path="/orgs/:org/audit" element={<OrgPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/account/activity" element={<ActivityPage />} />
          <Route path="/account/tokens" element={<TokensPage />} />
          <Route path="/account/password" element={<PasswordPage />} />
        </Route>
        <Route path="/orgs/:org/games/:game" element={<GameLayout />}>
          <Route index element={<GameHome />} />
          <Route path="branches/:branch" element={<ContentProvider><ExtensionsProvider><Outlet /><LiveOpsSettingsHost /></ExtensionsProvider></ContentProvider>}>
            <Route index element={<BranchIndex />} />
            <Route path="templates" element={<TemplatesPage />} />
            <Route path="templates/new" element={<TemplateEditorPage />} />
            <Route path="templates/:template" element={<TemplateEditorPage />} />
            <Route path="templates/:template/documents" element={<DocumentsPage />} />
            <Route path="enums" element={<EnumsPage />} />
            <Route path="enums/new" element={<EnumEditorPage />} />
            <Route path="enums/:enum" element={<EnumEditorPage />} />
            <Route path="profiles" element={<ProfilesPage />} />
            <Route path="tags" element={<TagsPage />} />
            <Route path="search" element={<SearchPage />} />
            <Route path="liveops/calendar" element={<CalendarPage />} />
            <Route path="liveops/daily-bonus" element={<DailyBonusPage />} />
            <Route path="liveops/segments" element={<SegmentsPage />} />
            <Route path="liveops/overrides" element={<OverridesPage />} />
            <Route path="liveops/ab-tests" element={<AbTestsPage />} />
            <Route path="liveops/events" element={<EventsPage />} />
            <Route path="liveops/offers" element={<OffersPage />} />
            <Route path="liveops/store" element={<StorePage />} />
            <Route path="liveops/sales" element={<SalesPage />} />
            <Route path="liveops/items" element={<DocumentsPage template="SmartObjects.Item" title="Items" />} />
            <Route path="liveops/store-items" element={<DocumentsPage template="SmartObjects.StoreItem" title="Store items" />} />
            <Route path="liveops/reward-tables" element={<DocumentsPage template="LiveOps.RewardTable" title="Reward tables" />} />
            <Route path="extensions/:name?" element={<ExtensionsPage />} />
            <Route path="localization/:tab?" element={<LocalizationPage />} />
            <Route path="assets/:tab?" element={<AssetsPage />} />
            <Route path="products" element={<ProductsPage />} />
            <Route path="history/:sha?" element={<HistoryPage />} />
            <Route path="releases/:tab?" element={<ReleasesPage />} />
            <Route path="merge-requests" element={<MergeRequestsPage />} />
            <Route path="merge-requests/:mr" element={<MergeRequestPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
          <Route path="settings/:tab?" element={<SettingsPage />} />
          <Route path="envs/:env">
            <Route path="platforms" element={<PlatformsPage />} />
            <Route path="settings" element={<EnvironmentSettingsPage />} />
            <Route path="players" element={<PlayersPage />} />
            <Route path="players/erasures/:receipt" element={<ReceiptPage />} />
            <Route path="players/:player" element={<PlayerPage />} />
            <Route path="orders" element={<OrdersPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
    </>
  );
}
